<template>
  <dashboard-layout title="Playbooks">
    <template #actions>
      <filter-chips
        v-if="playbooks.length > 1"
        :model-value="selectedId"
        :options="playbookOptions"
        @update:model-value="selectPlaybook"
      />
      <button
        v-if="selected"
        class="build-btn"
        :disabled="!slides.length"
        @click="buildRandomPost"
      >
        🎲 Build Random Post
      </button>
    </template>

    <loading-state v-if="loading" label="Loading playbooks…" />

    <empty-state v-else-if="!selected" title="No playbooks found" />

    <template v-else>
      <!-- Playbook identity + status + approve/reject -->
      <data-panel>
        <template #title>
          <span class="pb-title-text">{{ selected.title }}</span>
          <status-badge
            class="pb-title-badge"
            :variant="statusVariant(selected.status)"
            :label="formatStatus(selected.status)"
          />
        </template>
        <template #actions>
          <button
            v-if="selected.status !== 'approved'"
            class="action-btn action-approve"
            :disabled="pbUpdating"
            @click="setPlaybookStatus('approved')"
          >Approve playbook</button>
          <button
            v-if="selected.status !== 'rejected'"
            class="action-btn action-reject"
            :disabled="pbUpdating"
            @click="setPlaybookStatus('rejected')"
          >Reject</button>
        </template>
        <div class="pb-tags">
          <span class="pb-tag pb-tag-theme">{{ selected.theme }}</span>
          <span class="pb-tag">{{ selected.pillar }}</span>
          <span class="pb-tag">{{ selected.emotionalMechanic }}</span>
          <span class="pb-tag pb-tag-voice">{{ selected.narrativeIdentity }}</span>
        </div>
      </data-panel>

      <!-- Summary counts derived from the loaded slides -->
      <stat-grid>
        <stat-card label="Slides" :value="slides.length" icon="book" accent />
        <stat-card label="Overlays" :value="totalOverlays" icon="messagecircle" />
        <stat-card label="Approved" icon="clipboardcheck">
          <span class="stat-ok">{{ approvedOverlays }}</span>
        </stat-card>
        <stat-card label="Combos" icon="sliders">
          <span class="stat-combo">{{ formatCombos(comboCount) }}</span>
        </stat-card>
      </stat-grid>

      <!-- 6-slide board: each column = one fixed narrative slide, holding its overlay bucket -->
      <data-panel title="Slide board">
        <div class="slides-board">
          <div v-for="s in slides" :key="s.id" class="slide-column">
            <div class="slide-col-head">
              <div class="slide-col-titles">
                <span class="slide-col-num">Slide {{ s.slideNumber }}</span>
                <span class="slide-col-role">{{ s.roleLabel }}</span>
              </div>
              <span class="slide-col-goal">{{ s.goal }}</span>
            </div>

            <div class="visual-dir">
              <span class="visual-dir-label">Visual</span>
              <div class="visual-chips">
                <span v-for="(v, i) in s.visualDirection" :key="i" class="visual-chip">{{ v }}</span>
              </div>
            </div>

            <!-- Visual bucket: Nano Banana images, generated from dashboard (shown ABOVE text) -->
            <div class="visual-bucket-head">
              <span class="bucket-count">
                {{ imageApprovedCount(s) }}/{{ s.images?.length || 0 }} visuals
              </span>
              <button
                class="mini-btn gen-btn"
                :disabled="generatingImagesId === s.id"
                @click="generateImages(s)"
              >
                {{ generatingImagesId === s.id ? 'Generating…' : '✨ Generate visuals' }}
              </button>
            </div>

            <div v-if="generatingImagesId === s.id" class="img-progress">
              <div class="img-progress-bar"><div class="img-progress-fill"></div></div>
              <span class="img-progress-label">Nano Banana üretiyor — 30-90 sn sürebilir</span>
            </div>

            <div v-if="s.images?.length" class="img-grid">
              <div
                v-for="im in s.images"
                :key="im.id"
                :class="['img-card', `img-${im.status || 'pending'}`]"
              >
                <img :src="im.url" :alt="s.roleLabel" class="img-thumb" loading="lazy" @click="openLightbox(im.url)" />
                <div class="img-controls">
                  <button
                    :class="['ov-btn', 'ov-approve', { active: im.status === 'approved' }]"
                    :disabled="imageUpdating === im.id"
                    title="Approve"
                    @click="setImage(s, im, im.status === 'approved' ? 'pending' : 'approved')"
                  >✓</button>
                  <button
                    :class="['ov-btn', 'ov-reject', { active: im.status === 'rejected' }]"
                    :disabled="imageUpdating === im.id"
                    title="Reject"
                    @click="setImage(s, im, im.status === 'rejected' ? 'pending' : 'rejected')"
                  >✕</button>
                  <a class="ov-btn ov-copy" :href="im.url" target="_blank" rel="noopener" title="Open / download" download>⤓</a>
                </div>
              </div>
            </div>

            <div class="overlay-bucket-head">
              <span class="bucket-count">
                {{ overlayApprovedCount(s) }}/{{ s.overlays?.length || 0 }} approved
              </span>
              <div class="bucket-actions">
                <button class="mini-btn" :disabled="slideUpdating === s.id" @click="approveAll(s)">Approve all</button>
                <button class="mini-btn" @click="copyApproved(s)">
                  {{ copiedId === 'appr-' + s.id ? 'Copied' : 'Copy approved' }}
                </button>
              </div>
            </div>

            <div class="overlay-list">
              <div
                v-for="ov in (s.overlays || [])"
                :key="ov.id"
                :class="['overlay-chip', `ov-${ov.status || 'pending'}`]"
              >
                <p class="overlay-text">{{ ov.text }}</p>
                <div class="overlay-controls">
                  <button
                    :class="['ov-btn', 'ov-approve', { active: ov.status === 'approved' }]"
                    :disabled="overlayUpdating === ov.id"
                    title="Approve"
                    @click="setOverlay(s, ov, ov.status === 'approved' ? 'pending' : 'approved')"
                  >✓</button>
                  <button
                    :class="['ov-btn', 'ov-reject', { active: ov.status === 'rejected' }]"
                    :disabled="overlayUpdating === ov.id"
                    title="Reject"
                    @click="setOverlay(s, ov, ov.status === 'rejected' ? 'pending' : 'rejected')"
                  >✕</button>
                  <button
                    class="ov-btn ov-copy"
                    title="Copy"
                    @click="copyText(ov.text, ov.id)"
                  >{{ copiedId === ov.id ? '✓' : '⧉' }}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </data-panel>
    </template>

    <!-- Random post preview: one overlay per slide, shown as a readable 6-slide carousel -->
    <div v-if="postOpen" class="modal-overlay" @click.self="postOpen = false">
      <div class="post-modal">
        <div class="modal-header">
          <h2>Random Post Preview</h2>
          <button class="close-btn" @click="postOpen = false">×</button>
        </div>
        <p class="modal-hint">One overlay picked per slide. Read left → right as the carousel.</p>
        <div class="post-strip">
          <div v-for="(item, i) in post" :key="i" class="post-card">
            <div class="post-card-top">
              <span class="post-card-num">{{ item.slideNumber }}/6</span>
              <span class="post-card-role">{{ item.roleLabel }}</span>
            </div>
            <div class="post-card-body">
              <p class="post-card-text">{{ item.text }}</p>
            </div>
            <div class="post-card-visual">{{ item.visualDirection.join(' · ') }}</div>
          </div>
        </div>
        <div class="modal-actions">
          <button class="action-btn" @click="buildRandomPost">🎲 Reshuffle</button>
          <button class="action-btn action-generate" @click="copyPost">
            {{ copiedId === '__post__' ? 'Copied' : 'Copy this post' }}
          </button>
          <button class="action-btn" @click="postOpen = false">Close</button>
        </div>
      </div>
    </div>

    <!-- Image lightbox -->
    <div v-if="lightboxUrl" class="lightbox-overlay" @click="lightboxUrl = null">
      <img :src="lightboxUrl" class="lightbox-img" alt="preview" @click.stop />
      <a :href="lightboxUrl" target="_blank" rel="noopener" download class="lightbox-download" @click.stop>⤓ Download</a>
      <button class="lightbox-close" @click="lightboxUrl = null">×</button>
    </div>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { FirebasePlaybooksService } from '../services/firebasePlaybooksService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import StatusBadge from './ui/StatusBadge.vue'
import FilterChips from './ui/FilterChips.vue'
import LoadingState from './ui/LoadingState.vue'
import EmptyState from './ui/EmptyState.vue'

export default {
  name: 'PlaybooksDashboard',
  components: {
    DashboardLayout, StatGrid, StatCard, DataPanel, StatusBadge, FilterChips, LoadingState, EmptyState
  },
  setup() {
    const service = new FirebasePlaybooksService()
    const playbooks = ref([])
    const selectedId = ref(null)
    const slides = ref([])
    const loading = ref(true)
    const pbUpdating = ref(false)
    const slideUpdating = ref(null)
    const overlayUpdating = ref(null)
    const copiedId = ref(null)
    const postOpen = ref(false)
    const post = ref([])
    const generatingImagesId = ref(null)
    const imageUpdating = ref(null)
    const lightboxUrl = ref(null)
    let copyTimer = null

    const openLightbox = (url) => { lightboxUrl.value = url }

    const selected = computed(() => playbooks.value.find(p => p.id === selectedId.value) || null)

    const playbookOptions = computed(() =>
      playbooks.value.map(p => ({ value: p.id, label: p.title }))
    )

    const totalOverlays = computed(() =>
      slides.value.reduce((sum, s) => sum + (s.overlays?.length || 0), 0)
    )
    const approvedOverlays = computed(() =>
      slides.value.reduce((sum, s) => sum + (s.overlays || []).filter(o => o.status === 'approved').length, 0)
    )
    // Combinations using approved overlays per slide (fallback to total if a slide has none approved yet)
    const comboCount = computed(() =>
      slides.value.reduce((prod, s) => {
        const appr = (s.overlays || []).filter(o => o.status === 'approved').length
        const usable = appr > 0 ? appr : (s.overlays?.length || 1)
        return prod * usable
      }, 1)
    )

    const load = async () => {
      loading.value = true
      try {
        playbooks.value = await service.getAllPlaybooks()
        if (playbooks.value.length) {
          await selectPlaybook(playbooks.value[0].id)
        }
      } catch (e) {
        console.error('Error loading playbooks:', e)
      } finally {
        loading.value = false
      }
    }

    const selectPlaybook = async (id) => {
      selectedId.value = id
      slides.value = []
      try {
        slides.value = await service.getSlides(id)
      } catch (e) {
        console.error('Error loading slides:', e)
      }
    }

    const overlayApprovedCount = (s) => (s.overlays || []).filter(o => o.status === 'approved').length
    const imageApprovedCount = (s) => (s.images || []).filter(im => im.status === 'approved').length

    const generateImages = async (slide) => {
      if (generatingImagesId.value === slide.id) return
      generatingImagesId.value = slide.id
      try {
        const result = await service.generateSlideImages(selectedId.value, slide.id, undefined, undefined)
        const newImages = result?.images || []
        slide.images = [...(slide.images || []), ...newImages]
      } catch (e) {
        console.error('Image generation failed:', e)
        const parts = []
        if (e.code) parts.push(`Code: ${e.code}`)
        if (e.message) parts.push(`Message: ${e.message}`)
        if (e.details) parts.push(`Details: ${typeof e.details === 'string' ? e.details : JSON.stringify(e.details)}`)
        alert('Görsel üretimi başarısız.\n\n' + (parts.join('\n') || 'Unknown error'))
      } finally {
        generatingImagesId.value = null
      }
    }

    const setImage = async (slide, image, status) => {
      if (imageUpdating.value === image.id) return
      imageUpdating.value = image.id
      try {
        const updated = await service.setImageStatus(selectedId.value, slide.id, image.id, status)
        slide.images = updated
      } catch (e) {
        console.error('Image status update failed:', e)
        alert('Görsel durumu güncellenemedi.')
      } finally {
        imageUpdating.value = null
      }
    }

    const setOverlay = async (slide, overlay, status) => {
      if (overlayUpdating.value === overlay.id) return
      overlayUpdating.value = overlay.id
      try {
        const updated = await service.setOverlayStatus(selectedId.value, slide.id, overlay.id, status)
        slide.overlays = updated
      } catch (e) {
        console.error('Overlay update failed:', e)
        alert('Failed to update overlay.')
      } finally {
        overlayUpdating.value = null
      }
    }

    const approveAll = async (slide) => {
      if (slideUpdating.value === slide.id) return
      slideUpdating.value = slide.id
      try {
        const updated = await service.setAllOverlayStatus(selectedId.value, slide.id, 'approved')
        slide.overlays = updated
      } catch (e) {
        console.error('Approve all failed:', e)
        alert('Failed to approve all.')
      } finally {
        slideUpdating.value = null
      }
    }

    const setPlaybookStatus = async (status) => {
      if (pbUpdating.value) return
      pbUpdating.value = true
      try {
        await service.updatePlaybookStatus(selectedId.value, status)
        const p = playbooks.value.find(x => x.id === selectedId.value)
        if (p) p.status = status
      } catch (e) {
        console.error('Playbook status update failed:', e)
        alert('Failed to update playbook status.')
      } finally {
        pbUpdating.value = false
      }
    }

    const pickOne = (arr) => arr[Math.floor(Math.random() * arr.length)]

    // Build a random post: one overlay per slide (approved preferred, never rejected).
    const buildRandomPost = () => {
      post.value = slides.value.map(s => {
        const all = s.overlays || []
        const approved = all.filter(o => o.status === 'approved')
        const pool = approved.length ? approved : all.filter(o => o.status !== 'rejected')
        const usable = pool.length ? pool : all
        const chosen = usable.length ? pickOne(usable) : { text: '—' }
        return {
          slideNumber: s.slideNumber,
          roleLabel: s.roleLabel,
          visualDirection: s.visualDirection || [],
          text: chosen?.text || '—',
        }
      })
      postOpen.value = true
    }

    const copyText = async (text, id) => {
      if (!text) return
      try {
        await navigator.clipboard.writeText(text)
        copiedId.value = id
        if (copyTimer) clearTimeout(copyTimer)
        copyTimer = setTimeout(() => { copiedId.value = null }, 1500)
      } catch (e) {
        console.error('Copy failed:', e)
      }
    }

    const copyApproved = (slide) => {
      const approved = (slide.overlays || []).filter(o => o.status === 'approved').map(o => o.text)
      const list = approved.length ? approved : (slide.overlays || []).map(o => o.text)
      copyText(list.join('\n'), 'appr-' + slide.id)
    }

    const copyPost = () => {
      const lines = post.value.map(s => `Slide ${s.slideNumber} — ${s.roleLabel}\n${s.text}`)
      copyText(lines.join('\n\n'), '__post__')
    }

    const formatStatus = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Unknown'
    const statusVariant = (s) => {
      if (s === 'approved') return 'success'
      if (s === 'rejected') return 'danger'
      if (s === 'pending') return 'warning'
      return 'neutral'
    }
    const formatCombos = (n) => {
      if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
      if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K'
      return String(n)
    }

    onMounted(load)

    return {
      playbooks, selectedId, selected, slides, loading,
      pbUpdating, slideUpdating, overlayUpdating, copiedId,
      postOpen, post,
      generatingImagesId, imageUpdating,
      lightboxUrl, openLightbox,
      playbookOptions,
      totalOverlays, approvedOverlays, comboCount,
      selectPlaybook, overlayApprovedCount, imageApprovedCount,
      setOverlay, approveAll, generateImages, setImage,
      setPlaybookStatus, buildRandomPost, copyText, copyApproved, copyPost,
      formatStatus, statusVariant, formatCombos,
    }
  }
}
</script>

<style scoped>
/* Header action buttons */
.build-btn {
  padding: 8px 14px;
  background: rgba(108, 99, 255, 0.15);
  border: 1px solid rgba(108, 99, 255, 0.4);
  border-radius: 8px;
  color: #a78bfa;
  font-size: 13px;
  cursor: pointer;
}
.build-btn:hover:not(:disabled) { background: rgba(108, 99, 255, 0.25); }
.build-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* Playbook meta panel */
.pb-title-text { color: var(--text-primary); }
.pb-title-badge { margin-left: 10px; vertical-align: middle; }
.pb-tags { display: flex; gap: 6px; flex-wrap: wrap; }
.pb-tag {
  font-size: 11px;
  padding: 3px 9px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.06);
  color: var(--text-secondary);
}
.pb-tag-theme { background: rgba(244, 114, 182, 0.15); color: #f472b6; }
.pb-tag-voice { background: rgba(108, 99, 255, 0.15); color: #a78bfa; }

/* Stat card accents */
.stat-ok { color: var(--success); }
.stat-combo { color: var(--gradient-2); }

/* 6-slide board */
.slides-board {
  display: grid;
  grid-template-columns: repeat(6, minmax(240px, 1fr));
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 8px;
}
.slide-column {
  background: rgba(26, 27, 46, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}
.slide-col-head { display: flex; flex-direction: column; gap: 4px; }
.slide-col-titles { display: flex; align-items: baseline; gap: 8px; }
.slide-col-num {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary);
  opacity: 0.7;
}
.slide-col-role {
  font-size: 14px;
  font-weight: 700;
  color: #a78bfa;
}
.slide-col-goal { font-size: 11px; color: var(--text-secondary); line-height: 1.4; }

.visual-dir { display: flex; flex-direction: column; gap: 4px; }
.visual-dir-label {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary);
}
.visual-chips { display: flex; flex-wrap: wrap; gap: 4px; }
.visual-chip {
  font-size: 10px;
  padding: 2px 7px;
  border-radius: 5px;
  background: rgba(251, 146, 60, 0.1);
  color: #fb923c;
}

.overlay-bucket-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  padding-top: 6px;
  border-top: 1px dashed rgba(255, 255, 255, 0.08);
}
.bucket-count { font-size: 10px; color: #34d399; }
.bucket-actions { display: flex; gap: 4px; }
.mini-btn {
  font-size: 10px;
  padding: 3px 7px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  color: var(--text-secondary);
  cursor: pointer;
}
.mini-btn:hover:not(:disabled) { background: rgba(255, 255, 255, 0.1); color: var(--text-primary); }
.mini-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.overlay-list { display: flex; flex-direction: column; gap: 6px; }

/* Visual (image) bucket */
.visual-bucket-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  padding-top: 8px;
  margin-top: 4px;
  border-top: 1px dashed rgba(255, 255, 255, 0.08);
}
.gen-btn { border-color: rgba(108, 99, 255, 0.4); color: #a78bfa; }
.gen-btn:hover:not(:disabled) { background: rgba(108, 99, 255, 0.15); }
.img-progress { display: flex; flex-direction: column; gap: 4px; }
.img-progress-bar {
  height: 4px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 2px;
  overflow: hidden;
}
.img-progress-fill {
  height: 100%;
  width: 40%;
  background: linear-gradient(90deg, #6c63ff, #a78bfa);
  border-radius: 2px;
  animation: indeterminate 1.4s ease-in-out infinite;
}
@keyframes indeterminate {
  0% { margin-left: -40%; }
  100% { margin-left: 100%; }
}
.img-progress-label { font-size: 10px; color: var(--text-secondary); }
.img-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
}
.img-card {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.2);
}
.img-card.img-approved { border-color: #34d399; }
.img-card.img-rejected { border-color: #f87171; opacity: 0.5; }
.img-thumb {
  width: 100%;
  aspect-ratio: 4 / 5;
  object-fit: cover;
  display: block;
  cursor: zoom-in;
  transition: opacity 0.15s ease;
}
.img-thumb:hover { opacity: 0.85; }

/* Lightbox */
.lightbox-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.88);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  cursor: zoom-out;
  padding: 32px;
}
.lightbox-img {
  max-width: 92vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
  cursor: default;
}
.lightbox-download {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 16px;
  background: rgba(108, 99, 255, 0.85);
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  text-decoration: none;
}
.lightbox-download:hover { background: rgba(108, 99, 255, 1); }
.lightbox-close {
  position: fixed;
  top: 20px;
  right: 28px;
  background: none;
  border: none;
  color: #fff;
  font-size: 36px;
  line-height: 1;
  cursor: pointer;
  opacity: 0.8;
}
.lightbox-close:hover { opacity: 1; }
.img-controls {
  position: absolute;
  bottom: 4px;
  right: 4px;
  display: flex;
  gap: 3px;
}
.img-controls .ov-btn {
  width: 22px;
  height: 22px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
  text-decoration: none;
}
.overlay-chip {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-left: 3px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: all 0.15s ease;
}
.overlay-chip.ov-approved { border-left-color: #34d399; background: rgba(34, 197, 94, 0.05); }
.overlay-chip.ov-rejected { border-left-color: #f87171; background: rgba(239, 68, 68, 0.04); opacity: 0.6; }
.overlay-text { margin: 0; font-size: 12.5px; color: var(--text-primary); line-height: 1.4; }
.overlay-controls { display: flex; gap: 4px; justify-content: flex-end; }
.ov-btn {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: var(--text-secondary);
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s ease;
}
.ov-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.ov-approve.active { background: rgba(34, 197, 94, 0.2); border-color: #34d399; color: #34d399; }
.ov-approve:hover:not(:disabled) { border-color: #34d399; color: #34d399; }
.ov-reject.active { background: rgba(239, 68, 68, 0.2); border-color: #f87171; color: #f87171; }
.ov-reject:hover:not(:disabled) { border-color: #f87171; color: #f87171; }
.ov-copy:hover:not(:disabled) { border-color: #a78bfa; color: #a78bfa; }

.action-btn {
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.action-btn:hover:not(:disabled) { background: rgba(255, 255, 255, 0.1); }
.action-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.action-approve { border-color: rgba(34, 197, 94, 0.4); color: #34d399; }
.action-approve:hover:not(:disabled) { background: rgba(34, 197, 94, 0.15); }
.action-reject { border-color: rgba(239, 68, 68, 0.4); color: #f87171; }
.action-reject:hover:not(:disabled) { background: rgba(239, 68, 68, 0.15); }
.action-generate { border-color: rgba(108, 99, 255, 0.5); color: #a78bfa; }
.action-generate:hover:not(:disabled) { background: rgba(108, 99, 255, 0.15); }

/* Random post preview modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(6px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
}
.post-modal {
  background: rgba(26, 27, 46, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  padding: 22px;
  width: 100%;
  max-width: 1180px;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.modal-header { display: flex; justify-content: space-between; align-items: center; }
.modal-header h2 { margin: 0; font-size: 18px; color: var(--text-primary); }
.close-btn {
  background: none; border: none; color: var(--text-secondary);
  font-size: 24px; cursor: pointer; line-height: 1; padding: 0 6px;
}
.modal-hint { margin: 0; font-size: 12px; color: var(--text-secondary); }
.post-strip {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
}
.post-card {
  position: relative;
  aspect-ratio: 9 / 16;
  background: linear-gradient(165deg, #1a1430 0%, #0d0a1c 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  padding: 14px 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.post-card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.post-card-num {
  font-size: 10px;
  color: var(--text-secondary);
  opacity: 0.7;
  font-family: monospace;
}
.post-card-role {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 700;
  color: #a78bfa;
}
.post-card-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 2px;
}
.post-card-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  color: #f5f3ff;
  text-align: center;
  font-weight: 600;
  font-family: Georgia, 'Times New Roman', serif;
}
.post-card-visual {
  font-size: 9px;
  color: var(--text-secondary);
  opacity: 0.55;
  text-align: center;
  line-height: 1.3;
}
.modal-actions { display: flex; gap: 8px; justify-content: flex-end; flex-wrap: wrap; }

@media (max-width: 1024px) {
  .slides-board { grid-template-columns: repeat(6, minmax(220px, 1fr)); }
}
</style>
